import { setCalendarZone, validateCalendarState } from '../../shared/calendar';
import { importKey, type Sealed, seal, unseal } from '../../shared/crypto';
import type { FocusSession } from '../../shared/focus';
import { extractImages } from '../../shared/media';
import type {
  EntityValue,
  HeldOperation,
  Kind,
  LocalRecord,
  Mutation,
  RecordData,
} from '../../shared/model';
import { validateValue } from '../../shared/model';
import { type Change, expandChanges } from '../../shared/productivity';
import { initialRecords } from '../../shared/seed';
import { deletedValue, isWorkspaceTrash } from '../../shared/trash';

import { advanceLogoutEpoch, logoutEpoch } from './runtime';
import {
  database,
  deleteLocalRecords,
  forgetKey,
  getRecord,
  type LocalSession,
  localExclusive,
  localRecords,
  metadata,
  putRecord,
  putRecords,
  setMetadata,
} from './storage';

/** How often a visible window pulls other devices' edits. The phone uses the same. */
const pullInterval = 30_000;

export type WorkspaceState = {
  phase: 'loading' | 'locked' | 'ready';
  records: LocalRecord[];
  session?: LocalSession;
  error: string;
  notice: string;
  syncing: boolean;
  saving: number;
  /** Edits waiting to upload, not counting those held in `conflicts`. */
  pending: number;
  /** Records edited here and on another device, waiting for the owner to choose a version. */
  conflicts: number;
  offline: boolean;
  paused: boolean;
  lastSync?: string;
  persistent?: boolean;
  bootstrapComplete?: boolean;
};

export async function request<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    cache: 'no-store',
  });

  const data = await response.json();

  if (!response.ok) {
    throw Object.assign(new Error(data.message ?? data.error ?? 'Request failed'), {
      status: response.status,
      payload: data,
    });
  }

  return data as T;
}

/** How many edits can upload now, and how many wait for the owner to choose a version. */
function queueCounts(records: LocalRecord[]) {
  return {
    pending: records.filter(r => r.operationId && !isHeld(r)).length,
    conflicts: records.filter(isHeld).length,
  };
}

/** Waiting for the owner to choose, and so not uploaded. */
export function isHeld(record: LocalRecord) {
  return Boolean(record.competing || record.clash);
}

export class WorkspaceEngine {
  private state: WorkspaceState = {
    phase: 'loading',
    records: [],
    error: '',
    notice: '',
    syncing: false,
    saving: 0,
    pending: 0,
    conflicts: 0,
    offline: !navigator.onLine,
    paused: false,
  };

  private listeners = new Set<() => void>();
  private key?: CryptoKey;
  private generation = 0;
  private channel = new BroadcastChannel('cairn-workspace');
  private timer?: ReturnType<typeof setTimeout>;
  snapshot = () => this.state;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  private update(part: Partial<WorkspaceState>) {
    if (part.records) {
      const settings = part.records.find(r => r.kind === 'settings');

      setCalendarZone(
        settings ? (settings.value as { timezone: string }).timezone : 'Asia/Yerevan',
      );
    }

    this.state = { ...this.state, ...part };

    for (const fn of this.listeners) {
      fn();
    }
  }

  constructor() {
    this.channel.onmessage = event => {
      if (event.data === 'locked') {
        void this.lock('Signed out in another window.', false);
      }

      if (event.data === 'changed' && this.state.phase === 'ready') {
        void this.refreshLocal();
      }
    };

    window.addEventListener('online', () => {
      this.update({ offline: false });
      void this.sync();
    });

    window.addEventListener('offline', () => this.update({ offline: true }));

    window.addEventListener('focus', () => {
      void this.checkAccess();
    });

    // A tab brought back, or a window left in front while the phone is used,
    // never receives `focus`; without these it would show edits made on another
    // device only after its own next edit. A pull is a cursor delta, so cheap.
    // `visibilitychange` bubbles from the document to the window.
    window.addEventListener('visibilitychange', () => {
      if (globalThis.document?.visibilityState === 'visible') {
        void this.checkAccess();
      }
    });

    setInterval(() => {
      if (globalThis.document?.visibilityState !== 'hidden') {
        void this.checkAccess();
      }
    }, pullInterval);

    setInterval(() => {
      void this.checkExpiry();
    }, 1000);
  }

  private async refreshLocal() {
    const generation = this.generation,
      key = this.key,
      session = this.state.session;

    if (!key || !session || this.state.phase !== 'ready') {
      return;
    }

    const records = await localRecords(session.userId, key);

    if (generation === this.generation) {
      this.update({ records, ...queueCounts(records) });
    }
  }

  async boot() {
    try {
      const session = await metadata<LocalSession>('session'),
        key = await metadata<CryptoKey>('key');

      if (((await metadata<string>('logoutEpoch')) ?? '') !== logoutEpoch()) {
        await forgetKey();
        this.update({ phase: 'locked', notice: 'Signed out from Cairn or its vault.' });

        return;
      }

      if (
        !session ||
        !key ||
        session.signedOut ||
        !Number.isFinite(Date.parse(session.expiresAt)) ||
        Date.parse(session.expiresAt) <= Date.now()
      ) {
        await forgetKey();
        this.update({ phase: 'locked', session, records: [] });

        return;
      }

      await this.prepareWorkspaceDownload(session.userId);
      this.key = key;
      const records = await localRecords(session.userId, key);
      const bootstrapComplete = (await metadata<boolean>(`bootstrap:${session.userId}`)) ?? true;

      this.update({
        phase: 'ready',
        session,
        records,
        ...queueCounts(records),
        bootstrapComplete,
        notice: bootstrapComplete
          ? ''
          : 'Workspace download is incomplete. Go online to finish; downloaded records are read-only.',
      });

      await this.sync();
    } catch {
      this.update({
        phase: 'locked',
        error: 'Local storage could not be opened. Existing data has not been removed.',
      });
    }
  }

  async finishPendingLogout() {
    if (await metadata<boolean>('pendingLogout')) {
      await request('/api/auth/sign-out', {});
      await setMetadata('pendingLogout', false);
    }
  }

  async open() {
    const deviceId = (await metadata<string>('deviceId')) ?? crypto.randomUUID();

    await setMetadata('deviceId', deviceId);
    const result = await request<LocalSession & { key: string }>('/api/device', { deviceId });
    const key = await importKey(result.key);

    const session: LocalSession = {
      userId: result.userId,
      deviceId,
      name: result.name,
      email: result.email,
      expiresAt: result.expiresAt,
    };

    const db = await database(),
      tx = db.transaction('meta', 'readwrite');

    await tx.store.put(logoutEpoch(), 'logoutEpoch');
    await tx.store.put(key, 'key');
    await tx.store.put(session, 'session');
    await tx.done;
    await this.prepareWorkspaceDownload(session.userId);
    this.generation += 1;
    this.key = key;
    const bootstrapComplete = (await metadata<boolean>(`bootstrap:${session.userId}`)) ?? false;

    await setMetadata(`bootstrap:${session.userId}`, bootstrapComplete);
    const records = await localRecords(session.userId, key);

    this.update({
      phase: 'ready',
      session,
      records,
      ...queueCounts(records),
      bootstrapComplete,
      error: '',
      notice: '',
      paused: false,
    });

    await this.sync();

    if (
      this.state.phase === 'ready' &&
      this.state.records.length === 0 &&
      !this.state.error &&
      !this.state.offline &&
      this.state.lastSync
    ) {
      for (const item of initialRecords()) {
        await this.save(item.id, item.kind, item.value);
      }

      await this.sync();
    }
  }

  private async prepareWorkspaceDownload(owner: string) {
    // The old main cursor advanced past Vault changes without downloading them.
    // Reconcile from zero in a separate cursor namespace, keeping all local edits.
    if (
      (await metadata<number>(`cursor:${owner}`)) !== undefined &&
      (await metadata<number>(`cursor:combined:${owner}`)) === undefined
    ) {
      await setMetadata(`bootstrap:${owner}`, false);
    }
  }

  async save(id: string, kind: Kind, value: EntityValue, options: { automatic?: boolean } = {}) {
    return this.saveMany([{ id, kind, value }], options);
  }

  /** `automatic` marks writes the app makes by itself; see `LocalRecord.automatic`. */
  async saveMany(changes: Change[], options: { automatic?: boolean } = {}) {
    if (!this.key || !this.state.session || this.state.phase !== 'ready') {
      throw new Error('Sign in before editing');
    }

    if (this.state.bootstrapComplete === false) {
      throw new Error(
        'Finish downloading the workspace before editing. Downloaded records are read-only.',
      );
    }

    if (Date.now() >= Date.parse(this.state.session.expiresAt)) {
      await this.lock('Your seven-day session expired. Sign in online to continue.');

      throw new Error('Session expired');
    }

    const key = this.key,
      owner = this.state.session.userId,
      generation = this.generation;

    this.update({ saving: this.state.saving + 1, error: '' });

    try {
      await localExclusive(async () => {
        if (generation !== this.generation) {
          throw new Error('Session ended before the edit could be saved');
        }

        const records = await localRecords(owner, key);

        if (
          changes.some(
            change => records.some(r => r.id === change.id && r.deleted) && !change.deleted,
          )
        ) {
          throw new Error('This record was permanently deleted');
        }

        const extracted = await extractImages(records, changes);

        const expanded = expandChanges(
          records,
          extracted.map(change => ({
            ...change,
            value: validateValue(
              change.kind,
              change.deleted
                ? (() => {
                    if (!('trashed' in change.value) || !change.value.trashed) {
                      throw new Error('Only trashed records can be permanently deleted');
                    }

                    return deletedValue(change.kind, change.value);
                  })()
                : change.value,
            ),
          })),
        );

        if (expanded.some(change => change.kind === 'calendar')) {
          validateCalendarState([
            ...records.filter(r => !expanded.some(c => c.id === r.id)),
            ...expanded,
          ]);
        }

        const next: LocalRecord[] = expanded.map(change => {
          const previous = records.find(r => r.id === change.id);

          return {
            ...change,
            revision: previous?.revision ?? 0,
            baseRevision: previous?.baseRevision ?? 0,
            localVersion: (previous?.localVersion ?? 0) + 1,
            operationId: crypto.randomUUID(),
            conflict: previous?.conflict ?? false,
            // A further edit to a record awaiting a choice stays held with it.
            ...(previous?.competing ? { competing: previous.competing } : {}),
            ...(previous?.clash ? { clash: previous.clash } : {}),
            // Automatic only while nothing the owner wrote is waiting with it.
            automatic:
              options.automatic && (!previous?.operationId || previous.automatic)
                ? true
                : undefined,
            updatedAt: new Date().toISOString(),
          };
        });

        await putRecords(owner, next, key);
      });

      await this.refreshLocal();
      this.channel.postMessage('changed');
      clearTimeout(this.timer);
      this.timer = setTimeout(() => void this.sync(), 650);
    } catch (error) {
      this.update({
        error: `Could not save this edit locally: ${(error as Error).message}. Keep this window open and copy your text.`,
      });

      throw error;
    } finally {
      this.update({ saving: Math.max(0, this.state.saving - 1) });
    }
  }

  async purgeVault(ids: string[]) {
    return this.purge(ids, 'vault');
  }

  async purgeWorkspace(ids: string[]) {
    return this.purge(ids, 'workspace');
  }

  private async purge(ids: string[], scope: 'vault' | 'workspace') {
    if (!this.key || !this.state.session || this.state.phase !== 'ready') {
      throw new Error('Sign in first');
    }

    if (this.state.bootstrapComplete === false) {
      throw new Error('Finish downloading the workspace before emptying trash');
    }

    if (await this.checkExpiry()) {
      throw new Error('Session expired');
    }

    const key = this.key,
      owner = this.state.session.userId,
      generation = this.generation;

    this.update({ saving: this.state.saving + 1, error: '' });

    try {
      await localExclusive(async () => {
        if (generation !== this.generation) {
          throw new Error('Session ended before deletion could be saved');
        }

        const rows = await localRecords(owner, key);

        const changed = [...new Set(ids)].map(id => {
          const row = rows.find(r => r.id === id);

          if (
            !row ||
            (scope === 'workspace'
              ? !isWorkspaceTrash(row)
              : row.deleted ||
                row.kind !== 'vault' ||
                !('trashed' in row.value) ||
                !row.value.trashed)
          ) {
            throw new Error('Only items currently in this trash can be permanently deleted');
          }

          if (
            row.kind === 'task' &&
            (rows.some(
              r =>
                r.kind === 'focus' &&
                (r.value as FocusSession).taskId === id &&
                (r.value as FocusSession).endedAt === null,
            ) ||
              ('focus' in row.value && row.value.focus?.sessionOpen))
          ) {
            throw new Error('End the focus session before permanently deleting its task');
          }

          // Permanent deletion is final, so it settles any pending choice too.
          const { competing: _settled, clash: _clash, ...rest } = row;

          return {
            ...rest,
            value: deletedValue(row.kind, row.value),
            deleted: true,
            operationId: crypto.randomUUID(),
            localVersion: row.localVersion + 1,
          };
        });

        await putRecords(owner, changed, key);
      });

      await this.refreshLocal();
      this.channel.postMessage('changed');
      await this.sync();
    } finally {
      this.update({ saving: Math.max(0, this.state.saving - 1) });
    }
  }

  async sync() {
    if (
      !this.key ||
      !this.state.session ||
      this.state.phase !== 'ready' ||
      this.state.syncing ||
      this.state.paused
    ) {
      return;
    }

    if (!navigator.onLine) {
      this.update({ offline: true });

      return;
    }

    if (await this.checkExpiry()) {
      return;
    }

    const generation = this.generation,
      key = this.key,
      owner = this.state.session.userId;

    this.update({ syncing: true });

    try {
      // An edit held for the owner's choice is not sent again until they choose.
      const pending = (await localRecords(owner, key)).filter(r => r.operationId && !isHeld(r));

      const operations: Mutation[] = pending.map(r => ({
        id: r.id,
        kind: r.kind,
        value: r.value,
        operationId: r.operationId!,
        baseRevision: r.baseRevision,
        ...(r.deleted ? { deleted: true } : {}),
      }));

      type Result = {
        acknowledgements?: { id: string; revision: number; operationId: string }[];
        held?: HeldOperation[];
        records: RecordData[];
        cursor?: number;
        through?: number;
        more?: boolean;
        total?: number;
      };

      // Persisted key name from the unified-workspace rollout; not a runtime mode.
      const cursorKey = `cursor:combined:${owner}`,
        uploadKey = `upload:${owner}`;

      const cursor = (await metadata<number>(cursorKey)) ?? 0;
      let result: Result;
      const stored = await metadata<Sealed>(uploadKey);

      if (
        stored ||
        operations.length > 100 ||
        new TextEncoder().encode(JSON.stringify(operations)).length > 12 * 1024 * 1024
      ) {
        let upload: { transfer: string; pages: Mutation[][]; next: number };

        if (stored) {
          upload = await unseal(stored, key, uploadKey);
        } else {
          const pages: Mutation[][] = [];

          let page: Mutation[] = [],
            bytes = 2;

          for (const operation of operations) {
            const size = new TextEncoder().encode(JSON.stringify(operation)).length + 1;

            if (size > 12 * 1024 * 1024) {
              throw Object.assign(
                new Error(
                  'A single record exceeds the 12 MB sync limit. Export it and split the note.',
                ),
                { status: 413 },
              );
            }

            if (page.length && (page.length >= 100 || bytes + size > 12 * 1024 * 1024)) {
              pages.push(page);
              page = [];
              bytes = 2;
            }

            page.push(operation);
            bytes += size;
          }

          if (page.length) {
            pages.push(page);
          }

          upload = { transfer: crypto.randomUUID(), pages, next: 0 };
          await setMetadata(uploadKey, await seal(upload, key, uploadKey));
        }

        for (let index = upload.next; index < upload.pages.length; index++) {
          if (generation !== this.generation) {
            return;
          }

          await request('/api/sync/upload', {
            transfer: upload.transfer,
            page: index,
            total: upload.pages.length,
            operations: upload.pages[index],
          });

          upload.next = index + 1;
          await setMetadata(uploadKey, await seal(upload, key, uploadKey));

          this.update({
            notice: `Uploading batch ${index + 1} of ${upload.pages.length}. Changes remain saved on this device.`,
          });
        }

        result = await request<Result>('/api/sync/commit', {
          transfer: upload.transfer,
          cursor,
          holdConflicts: true,
        });

        if (generation !== this.generation) {
          return;
        }

        await (await database()).delete('meta', uploadKey);
      } else {
        result = await request<Result>('/api/sync', {
          operations,
          paged: true,
          cursor,
          holdConflicts: true,
        });
      }

      const acknowledgements = result.acknowledgements ?? [],
        held = result.held ?? [];

      for (;;) {
        if (generation !== this.generation) {
          return;
        }

        await localExclusive(async () => {
          if (generation !== this.generation) {
            return;
          }

          for (const ack of acknowledgements) {
            const current = await getRecord(owner, ack.id, key);

            if (current?.operationId) {
              await putRecord(
                owner,
                {
                  ...current,
                  operationId:
                    current.operationId === ack.operationId ? undefined : current.operationId,
                  revision: Math.max(current.revision, ack.revision),
                  baseRevision: Math.max(current.baseRevision, ack.revision),
                },
                key,
              );
            }
          }

          for (const hold of held) {
            await this.holdForChoice(owner, key, hold.record);
          }

          for (const remote of result.records) {
            const current = await getRecord(owner, remote.id, key);

            if (current && remote.revision < current.revision) {
              continue;
            }

            if (!current?.operationId || remote.deleted) {
              await putRecord(
                owner,
                {
                  ...remote,
                  baseRevision: remote.revision,
                  localVersion: current?.localVersion ?? 0,
                },
                key,
              );
            } else if (remote.revision > current.baseRevision) {
              // Another device saved this record after the version this edit
              // started from. Upload would be refused, so ask the owner now.
              await this.holdForChoice(owner, key, remote);
            }
          }

          if (result.cursor !== undefined) {
            await setMetadata(cursorKey, result.cursor);
          }

          await setMetadata(`bootstrap:${owner}`, !result.more);
          this.update({ bootstrapComplete: !result.more });
        });

        await this.refreshLocal();

        if (!result.more) {
          break;
        }

        this.update({
          notice: `Downloading workspace: ${this.state.records.length} records saved locally. Keep Cairn open to finish.`,
        });

        result = await request<Result>('/api/sync/page', {
          cursor: result.cursor,
          through: result.through,
        });
      }

      await this.refreshLocal();
      this.channel.postMessage('changed');
      this.update({ offline: false, error: '', notice: '', lastSync: new Date().toISOString() });
    } catch (error) {
      if (generation !== this.generation) {
        return;
      }

      if (
        error instanceof DOMException &&
        ['QuotaExceededError', 'UnknownError', 'InvalidStateError', 'AbortError'].includes(
          error.name,
        )
      ) {
        this.update({
          offline: false,
          error:
            'Local storage could not save the workspace download. Free device storage and retry sync; existing records and pending edits are retained.',
          notice: '',
        });
      } else if ((error as { status?: number }).status === 410) {
        await (await database()).delete('meta', `upload:${owner}`);

        this.update({
          offline: false,
          error: '',
          notice:
            'The staged upload expired. Restarting from encrypted records saved on this device.',
        });
      } else if ((error as { status?: number }).status === 409) {
        const payload = (error as { payload: { records?: RecordData[]; recordIds?: string[] } })
          .payload;

        let clashed = false;

        await (await database()).delete('meta', `upload:${owner}`);

        if (!payload.records) {
          let page = await request<{
            records: RecordData[];
            cursor: number;
            more: boolean;
            through: number;
          }>('/api/sync/page', { cursor: 0 });

          payload.records = [...page.records];

          while (page.more) {
            page = await request('/api/sync/page', { cursor: page.cursor, through: page.through });
            payload.records.push(...page.records);
          }
        }

        await localExclusive(async () => {
          if (generation !== this.generation) {
            return;
          }

          for (const remote of payload.records!) {
            const current = await getRecord(owner, remote.id, key);

            if (!current?.operationId && (!current || current.revision <= remote.revision)) {
              await putRecord(
                owner,
                {
                  ...remote,
                  baseRevision: remote.revision,
                  localVersion: current?.localVersion ?? 0,
                },
                key,
              );
            }
          }

          clashed = await this.holdClash(owner, key, payload.recordIds ?? [], payload.records!);
        });

        await this.refreshLocal();

        // A refused second session is put to the owner as a choice, not a message.
        this.update({ offline: false, error: clashed ? '' : (error as Error).message, notice: '' });
      } else if ((error as { status?: number }).status === 401) {
        await this.lock('Your session ended. Sign in online to continue.');
      } else {
        this.update({
          offline: !(error as { status?: number }).status,
          error: (error as { status?: number }).status ? (error as Error).message : '',
          notice:
            this.state.bootstrapComplete === false
              ? 'Workspace download is incomplete. Downloaded records are read-only until a connection returns.'
              : 'Server unavailable. Changes remain encrypted on this device.',
        });
      }
    } finally {
      if (generation === this.generation) {
        this.update({ syncing: false });

        if (this.state.pending && !this.state.offline && !this.state.error && !this.state.paused) {
          clearTimeout(this.timer);
          this.timer = setTimeout(() => void this.sync(), 650);
        }
      }
    }
  }

  /**
   * Settles a record that was edited here and on another device. `local`
   * uploads this device's version over the other, which the server keeps in
   * revision history; `remote` discards this device's edit for the other one.
   */
  async resolveConflict(id: string, keep: 'local' | 'remote') {
    if (!this.key || !this.state.session || this.state.phase !== 'ready') {
      throw new Error('Sign in before choosing a version');
    }

    if (await this.checkExpiry()) {
      throw new Error('Session expired');
    }

    const key = this.key,
      owner = this.state.session.userId,
      generation = this.generation;

    await localExclusive(async () => {
      if (generation !== this.generation) {
        throw new Error('Session ended before the choice could be saved');
      }

      const current = await getRecord(owner, id, key);

      if (current?.clash) {
        await this.settleClash(owner, key, current, keep);

        return;
      }

      // Another window may already have settled it.
      if (!current?.competing) {
        return;
      }

      const { competing, ...edit } = current;

      await putRecord(
        owner,
        keep === 'remote'
          ? {
              ...competing,
              baseRevision: competing.revision,
              localVersion: current.localVersion + 1,
            }
          : {
              ...edit,
              revision: competing.revision,
              baseRevision: competing.revision,
              operationId: crypto.randomUUID(),
              localVersion: current.localVersion + 1,
              updatedAt: new Date().toISOString(),
            },
        key,
      );
    });

    await this.refreshLocal();
    this.channel.postMessage('changed');

    if (keep === 'local') {
      await this.sync();
    }
  }

  /**
   * After the server refused a session because another device has one open,
   * marks this device's never-uploaded session with the other one. Returns
   * whether it did. Must run inside `localExclusive`.
   */
  private async holdClash(owner: string, key: CryptoKey, ids: string[], records: RecordData[]) {
    let clashed = false;

    for (const id of ids) {
      const mine = await getRecord(owner, id, key);

      if (
        !mine?.operationId ||
        mine.revision !== 0 ||
        mine.kind !== 'focus' ||
        (mine.value as FocusSession).endedAt !== null
      ) {
        continue;
      }

      const other = records.find(
        r =>
          ids.includes(r.id) &&
          r.id !== id &&
          r.kind === 'focus' &&
          (r.value as FocusSession).endedAt === null,
      );

      if (other) {
        await putRecord(owner, { ...mine, clash: other }, key);
        clashed = true;
      }
    }

    return clashed;
  }

  /**
   * `local` keeps this device's session and ends the other, leaving its open
   * interval unconfirmed for review; `remote` drops this device's session,
   * which never reached the server. Must run inside `localExclusive`.
   */
  private async settleClash(
    owner: string,
    key: CryptoKey,
    mine: LocalRecord,
    keep: 'local' | 'remote',
  ) {
    const { clash, ...session } = mine;

    if (!clash) {
      return;
    }

    if (keep === 'remote') {
      await deleteLocalRecords(owner, [mine.id], key);

      return;
    }

    const other = (await getRecord(owner, clash.id, key)) ?? {
      ...clash,
      baseRevision: clash.revision,
      localVersion: 0,
    };

    const now = Date.now(),
      value = other.value as FocusSession;

    await putRecords(
      owner,
      [
        { ...session, localVersion: mine.localVersion + 1 },
        {
          ...other,
          value: {
            ...value,
            endedAt: value.endedAt ?? now,
            segments: value.segments.map(segment =>
              segment.endedAt === null
                ? {
                    ...segment,
                    endedAt: now,
                    confirmed: false,
                    correctionNote:
                      'Ended when a session was kept on another device. Confirm or correct this interval.',
                  }
                : segment,
            ),
          },
          operationId: crypto.randomUUID(),
          localVersion: other.localVersion + 1,
          updatedAt: new Date(now).toISOString(),
        },
      ],
      key,
    );
  }

  /**
   * Keeps this device's unsent edit and sets the server's version beside it.
   * Called when the server holds an edit back, and when a download shows that
   * another device saved the record after this edit began. Must run inside
   * `localExclusive`.
   */
  private async holdForChoice(owner: string, key: CryptoKey, remote: RecordData) {
    const current = await getRecord(owner, remote.id, key);

    // A pending permanent deletion is final and uploads regardless.
    if (!current || current.deleted) {
      return;
    }

    // Nothing the owner wrote is waiting, only the app's own heartbeat: take
    // the other device's version rather than asking about it.
    if (current.operationId && current.automatic) {
      await putRecord(
        owner,
        { ...remote, baseRevision: remote.revision, localVersion: current.localVersion + 1 },
        key,
      );

      return;
    }

    if (!current.operationId) {
      if (remote.revision >= current.revision) {
        await putRecord(
          owner,
          { ...remote, baseRevision: remote.revision, localVersion: current.localVersion },
          key,
        );
      }

      return;
    }

    if (!current.competing || current.competing.revision < remote.revision) {
      await putRecord(owner, { ...current, competing: remote }, key);
    }
  }

  async lock(message = '', broadcast = true) {
    if (broadcast) {
      advanceLogoutEpoch();
    }

    this.generation += 1;
    clearTimeout(this.timer);
    this.key = undefined;

    this.update({
      phase: 'locked',
      records: [],
      pending: 0,
      conflicts: 0,
      syncing: false,
      notice: message,
    });

    await localExclusive(forgetKey);

    if (broadcast) {
      this.channel.postMessage('locked');
    }
  }

  async signOut() {
    // Flush local writes; retain encrypted pending edits even if the network is absent.
    await localExclusive(async () => {});
    await setMetadata('pendingLogout', true);
    await this.lock('Signed out. Your encrypted notes and pending edits are retained.');

    try {
      await this.finishPendingLogout();
    } catch {
      /* Revocation will be retried before the next online sign-in. */
    }
  }

  private async checkExpiry() {
    if (
      this.state.phase === 'ready' &&
      ((await metadata<string>('logoutEpoch')) ?? '') !== logoutEpoch()
    ) {
      await this.lock('Signed out from Cairn or its vault.', false);

      return true;
    }

    if (
      this.state.phase === 'ready' &&
      this.state.session &&
      Date.now() >= Date.parse(this.state.session.expiresAt)
    ) {
      await this.lock('Your seven-day session expired. Sign in online to continue.');

      return true;
    }

    return false;
  }

  private async checkAccess() {
    if (!(await this.checkExpiry())) {
      await this.sync();
    }
  }

  pauseSync(value: boolean) {
    this.update({ paused: value });

    if (!value) {
      void this.sync();
    }
  }

  clearNotice() {
    this.update({ notice: '', error: '' });
  }

  async requestPersistence() {
    const persistent = (await navigator.storage?.persist?.()) ?? false;

    this.update({
      persistent,
      notice: persistent
        ? 'Persistent storage granted by this browser.'
        : 'This browser did not grant persistent storage. Keep server backups and exports.',
    });
  }
}

export const workspace = new WorkspaceEngine();
